import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { createResellerMerchants, getAllUsers } from "../../../config/LiveApi";

/* -------- Commission Normalizer -------- */
const normalizeCommission = (value) => {
  if (value === "" || value === null || value === undefined) return "";

  let num = String(value).trim();

  if (num.startsWith(".")) {
    num = "0" + num;
  }

  const parsed = parseFloat(num);

  if (isNaN(parsed)) return "";

  return parsed;
};

const AddResellerMerchantModal = ({ reseller, token, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    merchantId: "",
    payinCommission: "",
    payoutCommission: "",
  });

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);

  const fetchAllUsers = async () => {
    try {
      setFetchingUsers(true);
      const response = await getAllUsers(token, 1);
      const adminUsers = response.data.filter((item) => item.role === "admin");
      setUsers(adminUsers);
    } catch (err) {
      toast.error(err?.message || "Failed to fetch users");
    } finally {
      setFetchingUsers(false);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, [token]);

  const handleUserSelect = (e) => {
    setForm((prev) => ({
      ...prev,
      merchantId: e.target.value,
    }));
  };

  const handleInput = (e) => {
    const { name, value } = e.target;

    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      if (!form.merchantId) {
        toast.error("Please select a merchant");
        return;
      }

      const payin = normalizeCommission(form.payinCommission);
      const payout = normalizeCommission(form.payoutCommission);

      if (payin === "" || payout === "") {
        toast.error("Commission must be a valid number");
        return;
      }

      if (payin < 0 || payin > 100 || payout < 0 || payout > 100) {
        toast.error("Commission must be between 0 and 100");
        return;
      }

      setLoading(true);

      const payload = {
        merchantId: form.merchantId,
        payinCommission: String(payin),
        payoutCommission: String(payout),
        resellerId: reseller.id,
      };

      const response = await createResellerMerchants(payload, token);

      if (!response?.status) {
        toast.error(response?.message || "Unable to add merchant");
        return;
      }

      toast.success("Reseller Merchant added successfully");
      onSuccess();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to add reseller merchant",
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h3>Add Merchant</h3>
          <span style={closeStyle} onClick={onClose}>
            ✖
          </span>
        </div>

        <p style={{ fontSize: "14px", color: "#666" }}>
          Reseller: <strong>{reseller.name}</strong>
        </p>

        <div style={formGroup}>
          <label>Select Merchant</label>
          <select
            onChange={handleUserSelect}
            style={inputStyle}
            defaultValue=""
            disabled={fetchingUsers}
          >
            <option value="" disabled>
              {fetchingUsers ? "Loading users..." : "Select Merchant"}
            </option>

            {users.map((item) => (
              <option key={item.id} value={item.id}>
                {item.alias || item.id || "N/A"}
              </option>
            ))}
          </select>
        </div>

        <div style={formRow}>
          <div style={{ flex: 1 }}>
            <label>Payin Commission (%)</label>
            <input
              name="payinCommission"
              value={form.payinCommission}
              onChange={handleInput}
              style={inputStyle}
              placeholder="Enter payin commission"
            />
          </div>

          <div style={{ flex: 1 }}>
            <label>Payout Commission (%)</label>
            <input
              name="payoutCommission"
              value={form.payoutCommission}
              onChange={handleInput}
              style={inputStyle}
              placeholder="Enter payout commission"
            />
          </div>
        </div>

        <div style={buttonRow}>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={primaryButton}
          >
            {loading ? "Adding..." : "Add Merchant"}
          </button>

          <button onClick={onClose} style={secondaryButton}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Styles ---------------- */

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modalStyle = {
  backgroundColor: "#fff",
  padding: "30px",
  width: "520px",
  borderRadius: "14px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "15px",
};

const closeStyle = {
  cursor: "pointer",
  fontSize: "18px",
};

const formGroup = {
  marginBottom: "15px",
  display: "flex",
  flexDirection: "column",
};

const formRow = {
  display: "flex",
  gap: "15px",
  marginBottom: "15px",
};

const inputStyle = {
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  marginTop: "5px",
  fontSize: "14px",
};

const buttonRow = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  marginTop: "20px",
};

const primaryButton = {
  padding: "10px 20px",
  backgroundColor: "#00C389",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
};

const secondaryButton = {
  padding: "10px 20px",
  backgroundColor: "#e5e7eb",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
};

export default AddResellerMerchantModal;
